import parse from "html-react-parser";
import React, { useState } from "react";
import { Alert, Card } from "react-bootstrap";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useAppApi, useAppContext } from "@/App";
import { useRequest } from "@components/AppHook";
import { DndTable } from "@components/DND";
import { DevForm } from "@components/Forms/Fields";
import { RenderForm } from "@components/Forms/Render";
import { GameToBrowserButton, GameToGameCenterButton } from "@components/Game/Misc";
import { Awesome, DevModal, Hint, YesNoColumn } from "@components/Helpers";
import { ModalDelete } from "@components/Modals";
import { CommonCard } from "@components/Pages";
import { url_reverse } from "@components/Utils";

function GameBoxInfoBlock() {
    const { t } = useTranslation();
    return (
        <div className="dmg-callout dmg-callout-info">
            <p>{t("В этой коробке скрипт автоматически управляет публикацией игр по следующим правилам:")}</p>
            <ol>
                <li>
                    {t("Если есть опубликованная игра категории ")} <strong>{"original:"}</strong>
                    {}
                    <ul>
                        <li>
                            {t("Игры ")} <strong>{"Buka "}</strong>
                            {t("и ")} <strong>{"Enaza "}</strong>
                            {t(
                                "снимаются с публикации и заносятся в черный список, чтобы блокировать обновления из API поставщика."
                            )}
                        </li>
                        <li>
                            {t("Игры ")}
                            <strong>{t("VK Play (Гифт, Ключ, Облако) ")}</strong> {t("снимаются с публикации.")}
                        </li>
                    </ul>
                </li>
                <li>
                    {t("Если нет опубликованной игры категории ")} <strong>{"original:"}</strong>
                    <ul>
                        <li>
                            {t("Игры ")} <strong>{"Buka "}</strong>
                            {t("и ")} <strong>{"Enaza "}</strong>
                            {t(
                                "возвращаются из черного списка, скрипт запрашивает обновления из API поставщика только если поле «Дата первой публикации» заполнено."
                            )}
                        </li>
                        <li>
                            {t("Игры ")}
                            <strong>{t("VK Play (Гифт и Ключ) ")}</strong>{" "}
                            {t("публикуются автоматически только если поле «Дата первой публикации» заполнено.")}
                        </li>
                    </ul>
                </li>
                <li>
                    {t("Если опубликована Облачная игра и есть опубликованные игры категорий ")}
                    <strong>{t("Original, Buka, Enaza, VK Play (Гифт и Ключ) ")}</strong>
                    {t("— Облачная игра снимается с публикации.")}{" "}
                </li>
                <li>
                    <Trans>
                        Если в коробке нет опубликованных игр категорий{" "}
                        <strong>Buka, Enaza, VK Play (Гифт, Ключ)</strong>, при этом есть
                        <strong>VK Play (Облако)</strong> — такая Облачная игра публикуется автоматически только если
                        поле «Дата первой публикации» заполнено.
                    </Trans>
                </li>
            </ol>
            <p>
                <strong>{t("Особенности ручного управления:")}</strong>
            </p>
            <ul>
                <li>
                    {t("Если у игры включено ручное управление, скрипт ")}
                    <strong>{t("не будет автоматически менять публикацию ")}</strong> {t("этой игры.")}{" "}
                    {t("Изменить управление можно в разделе ")}
                    <strong>{t('Администрирование → флаг "Ручное управление в коробке".')}</strong>
                </li>
            </ul>
        </div>
    );
}

export default function GameBox({ gameId, header }) {
    const { t } = useTranslation();
    const { apiData, setApiData, apiUrl, setApiReget } = useAppApi(
        url_reverse("api:game:gamebox", { game_id: gameId })
    );
    const { setDelData } = useAppContext();
    const { api_post } = useRequest();
    const [addGame, setAddGame] = useState({});

    if (!apiData) return;

    const createGameBox = (ev) => {
        api_post(ev, apiUrl, {
            params: { action: "create_gamebox" },
            then: (resp) => {
                if (resp.status === "ok") {
                    setApiData(resp);
                }
            },
        });
    };

    const addGameToBox = (props) => {
        if (!props.val) return;
        api_post(null, apiUrl, {
            params: { action: "add_game_to_gamebox", game_id: props.val },
            then: (resp) => {
                if (resp.status === "ok") {
                    setApiData(resp);
                    setAddGame({});
                } else if (resp.box_err) {
                    setAddGame({ show: true, error: resp.box_err });
                }
            },
        });
    };

    return (
        <>
            <CommonCard header={header}>
                <GameBoxInfoBlock />
                {apiData.object_list?.length > 0 ? (
                    <RenderForm />
                ) : (
                    <DevForm.Button variant="dark" name="create_gamebox" onClick={createGameBox}>
                        {t("Создать коробку")}
                    </DevForm.Button>
                )}
            </CommonCard>

            {apiData.object_list?.length > 0 && (
                <Card className="mt-4">
                    <Card.Header>
                        {t("Игры")}
                        <div className="dmg-card-header-tools">
                            <DevForm.Button
                                size="sm"
                                variant="dark"
                                name="add_game"
                                onClick={() => setAddGame({ show: true })}
                            >
                                <Awesome type="plus-circle" /> {t("Добавить")}
                            </DevForm.Button>
                        </div>
                    </Card.Header>
                    <Card.Body>
                        <DndTable
                            apiData={apiData}
                            setApiData={setApiData}
                            apiUrl={apiUrl}
                            postAction="rearrange_games"
                            ths={
                                <>
                                    <th>{t("Название")}</th>
                                    <th>{t("Категория")}</th>
                                    <th>{t("Тип продукта")}</th>
                                    <th>{t("Публикация в каталоге VK Play")}</th>
                                    <th>
                                        {t("Ручное управление ")}
                                        <Hint
                                            tooltip={t(
                                                "Источник: чекбокс «Ручное управление в коробке». Включено → скрипт не будет автоматически публиковать или снимать с публикации игру. Изменить можно в разделе Администрирование."
                                            )}
                                        />
                                    </th>
                                    <th />
                                </>
                            }
                            tds={(el) => (
                                <>
                                    <td>
                                        <Link to={url_reverse("games:admin", { game_id: el.__game.id })}>
                                            {el.__game.base_locale_object.name}
                                        </Link>
                                    </td>
                                    <td>{el.kind}</td>
                                    <td>{el.product_type}</td>
                                    <td>
                                        <YesNoColumn attr={el.__game.catalog_is_published} />
                                    </td>
                                    <td>
                                        <YesNoColumn attr={el.__game.is_control_in_box} />
                                    </td>
                                    <td className="dmg-table-row-tools">
                                        <GameToBrowserButton game={el.__game} />
                                        <GameToGameCenterButton game={el.__game} />
                                    </td>
                                    <td className="dmg-table-row-tools">
                                        <Awesome
                                            type="trash-o"
                                            color="red"
                                            onClick={() =>
                                                setDelData({
                                                    url: url_reverse("api:game:gamebox", {
                                                        game_id: el.__game.id,
                                                    }),
                                                    afterRespOK: () => {
                                                        setApiReget(true);
                                                    },
                                                })
                                            }
                                        />
                                    </td>
                                </>
                            )}
                        />
                    </Card.Body>
                </Card>
            )}

            <ModalDelete />

            <DevModal
                size="lg"
                show={addGame.show}
                onHide={() => setAddGame({})}
                header={t("Добавить игру")}
                body={
                    <>
                        {addGame.error && <Alert variant="danger">{parse(addGame.error)}</Alert>}
                        <DevForm.Field prefix="add_game" name="game" onChange={addGameToBox} />
                    </>
                }
            />
        </>
    );
}
